import { Fragment, useEffect, useMemo, useState } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { saveAs } from "file-saver";
import { getDepositRegister, exportDepositRegisterExcel } from "../TenantForm/Helpers/api";
import { formatDay, formatMoney, safeFileName, todayYmd } from "./reportFormat";
import "./rentRoll.css";

const GROUPS = [
  { id: "CURRENT", label: "All units (current)", dateLabel: "" },
  { id: "MOVE_OUT", label: "Move-outs without a completed SOA", dateLabel: "Moved out" },
  { id: "FUTURE", label: "Future leases", dateLabel: "Lease starts" },
];

const LOOK_BACK = [
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 24 months" },
  { value: 0, label: "All on record" },
];

const SOA = {
  NONE: { label: "No SOA", cls: "rr-pill--warn" },
  IN_APPROVAL: { label: "SOA in approval", cls: "rr-pill--pending" },
};

const sum = (rows, key) => rows.reduce((total, r) => total + (Number(r[key]) || 0), 0);

/**
 * The Security Deposit Register (CFO, 2026-10-06): what the company holds for this building
 * on a date — occupied units, tenancies that ended without a completed statement of account,
 * and signed leases for new residents. Only refundable money is totalled.
 */
const SecurityDepositRegisterTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [date, setDate] = useState(todayYmd());
  const [moveOutMonths, setMoveOutMonths] = useState(12);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const params = useMemo(() => ({ date, moveOutMonths }), [date, moveOutMonths]);

  useEffect(() => {
    let active = true;
    if (!mastersheetId || !date) return undefined;
    setLoading(true);
    setError(null);
    getDepositRegister(mastersheetId, params)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load the register");
        setReport(resp.data);
      })
      .catch((e) => active && setError(e?.response?.data?.message || e.message || "Failed to load the register"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [mastersheetId, date, params]);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await exportDepositRegisterExcel(mastersheetId, params);
      saveAs(blob, `SecurityDepositRegister_${safeFileName(masterSheetData?.building_name)}_${date}.xlsx`);
    } catch (e) {
      setError("Could not download the Excel file. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const s = report?.summary;
  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        rows: (report?.rows || []).filter((r) => r.group === g.id && !(hideEmpty && g.id === "CURRENT" && !r.refundable)),
      })).filter((g) => g.rows.length),
    [report, hideEmpty]
  );

  return (
    <div data-testid="deposit-register">
      <div className="rr-controls">
        <div>
          <label htmlFor="sdr-date">As of</label>
          <input
            id="sdr-date" type="date" className="form-control" value={date} max={todayYmd()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="sdr-window">Move-outs</label>
          <select id="sdr-window" className="form-select" value={moveOutMonths} onChange={(e) => setMoveOutMonths(Number(e.target.value))}>
            {LOOK_BACK.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="form-check mb-1">
          <input className="form-check-input" type="checkbox" id="sdr-hide-empty" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
          <label className="form-check-label" htmlFor="sdr-hide-empty" style={{ textTransform: "none", letterSpacing: 0, fontSize: 13, fontWeight: 500, color: "#0f1f35" }}>
            Hide units holding no deposit
          </label>
        </div>
        <div className="rr-controls__end">
          {loading && report ? <Spinner size="sm" color="primary" aria-label="Refreshing" /> : null}
          <button type="button" className="rpt-action rpt-action--primary" onClick={handleDownload} disabled={downloading || !report}>
            {downloading ? <Spinner size="sm" /> : <><FeatherIcon icon="download" size={14} /> Download Excel</>}
          </button>
        </div>
      </div>

      {error ? <p className="text-danger" role="alert">{error}</p> : null}
      {loading && !report ? <div className="text-center p-4"><Spinner color="primary" /> Building the register…</div> : null}

      {report && s && (
        <>
          <div className="rr-summary" data-testid="deposit-register-summary">
            <div className="rr-summary__cell">
              <div className="rr-summary__label">Current occupied units</div>
              <div className="rr-summary__value">{formatMoney(s.current.amount)}</div>
              <div className="rr-summary__sub">{s.current.units} units holding a deposit</div>
            </div>
            <div className="rr-summary__cell">
              <div className="rr-summary__label">Move-outs not yet processed</div>
              <div className="rr-summary__value">{formatMoney(s.moveOuts.amount)}</div>
              <div className="rr-summary__sub">{s.moveOuts.units} without a completed SOA</div>
            </div>
            <div className="rr-summary__cell">
              <div className="rr-summary__label">Future leases (signed)</div>
              <div className="rr-summary__value">{formatMoney(s.future.amount)}</div>
              <div className="rr-summary__sub">{s.future.units} new residents</div>
            </div>
            <div className="rr-summary__cell is-total">
              <div className="rr-summary__label">Total held</div>
              <div className="rr-summary__value">{formatMoney(s.total)}</div>
              <div className="rr-summary__sub">as of {formatDay(report.asOfDate)}</div>
            </div>
          </div>

          <div className="rr-scroll" tabIndex={0} role="region" aria-label={`Security deposit register as of ${formatDay(report.asOfDate)}`}>
            <table className="rr-table">
              <thead>
                <tr className="rr-head rr-head--solo">
                  <th scope="col" className="rr-pin">Unit #</th>
                  <th scope="col">Tenant</th>
                  <th scope="col" className="is-num">Security deposit</th>
                  <th scope="col" className="is-num">Pet deposit</th>
                  <th scope="col" className="is-num">Refundable total</th>
                  <th scope="col" className="is-num">Non-refundable</th>
                  <th scope="col">Date</th>
                  <th scope="col">SOA</th>
                </tr>
              </thead>
              <tbody>
                {groups.length ? groups.map((g) => (
                  <Fragment key={g.id}>
                    <tr className="is-group">
                      <td colSpan={8}>{g.label} · {g.rows.length}</td>
                    </tr>
                    {g.rows.map((r, i) => (
                      <tr key={`${g.id}-${r.unitNo}-${i}`} className={g.id === "CURRENT" && r.tenant === "Vacant" ? "is-vacant" : ""}>
                        <td className="rr-pin">{r.unitNo}</td>
                        <td>{r.tenant}</td>
                        <td className="is-num">{formatMoney(r.security)}</td>
                        <td className="is-num">{r.pet ? formatMoney(r.pet) : <span className="rr-muted">—</span>}</td>
                        <td className="is-num"><strong>{formatMoney(r.refundable)}</strong></td>
                        <td className="is-num">{r.nonRefundable ? formatMoney(r.nonRefundable) : <span className="rr-muted">—</span>}</td>
                        <td>{r.date ? <>{g.dateLabel} {formatDay(r.date)}</> : <span className="rr-muted">—</span>}</td>
                        <td>{r.soa ? <span className={`rr-pill ${SOA[r.soa]?.cls || ""}`}>{SOA[r.soa]?.label || r.soa}</span> : <span className="rr-muted">—</span>}</td>
                      </tr>
                    ))}
                    <tr className="is-subtotal">
                      <td className="rr-pin" />
                      <td>Subtotal</td>
                      <td className="is-num" />
                      <td className="is-num" />
                      <td className="is-num">{formatMoney(sum(g.rows, "refundable"))}</td>
                      <td className="is-num">{formatMoney(sum(g.rows, "nonRefundable"))}</td>
                      <td />
                      <td />
                    </tr>
                  </Fragment>
                )) : (
                  <tr><td colSpan={8} className="text-center rr-muted">No deposits to show.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td className="rr-pin">Total</td>
                  <td />
                  <td />
                  <td />
                  <td className="is-num">{formatMoney(s.total)}</td>
                  <td className="is-num">{formatMoney(s.nonRefundable)}</td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="rr-note">
            The total is refundable money only: security plus pet deposits. Non-refundable amounts are shown for
            reference. A move-out leaves the register once its statement of account is fully approved; a renewal is
            not a future lease, because its deposit is already held.
          </p>
        </>
      )}
    </div>
  );
};

export { SecurityDepositRegisterTab };
